import { api, queryKeys, replyInput, type ReplyInput } from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { formatDateTime, statusTone } from '../../lib/format';
import { Button, Card, Chip, FormGroup, Spinner, Textarea } from '../../ui';

export function TicketDetail() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();

  const { data: ticket, isLoading } = useQuery({
    queryKey: queryKeys.ticket(id),
    queryFn: () => api.support.adminShow(id).then((r) => r.data),
    enabled: !!id,
  });

  const form = useForm<ReplyInput>({ resolver: zodResolver(replyInput), defaultValues: { message: '' } });

  const reply = useMutation({
    mutationFn: (values: ReplyInput) => api.support.adminReply(id, values),
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: queryKeys.ticket(id) });
    },
  });

  if (isLoading) return <Spinner />;
  if (!ticket) return null;

  return (
    <>
      <PageHeader
        title={ticket.subject}
        description={`Ticket #${ticket.id} · ${ticket.user_code ?? ''}`}
        actions={<Chip tone={statusTone(ticket.status)}>{ticket.status}</Chip>}
      />

      <Card>
        <div className="ticket-thread">
          <article className="ticket-message">
            <header>
              <strong>{ticket.user_code ?? 'Member'}</strong>
              <span className="text-muted small">{formatDateTime(ticket.created_at)}</span>
            </header>
            <p>{ticket.message}</p>
          </article>

          {ticket.replies?.map((r) => (
            <article key={r.id} className={`ticket-message ${r.author_type}`}>
              <header>
                <strong>{r.author_name}</strong>
                <span className="text-muted small">{formatDateTime(r.created_at)}</span>
              </header>
              <p>{r.message}</p>
            </article>
          ))}
        </div>
      </Card>

      <Card>
        <h3 className="card-title">Reply</h3>
        <form onSubmit={form.handleSubmit((values) => reply.mutate(values))} noValidate>
          <FormGroup label="Message" error={form.formState.errors.message?.message}>
            <Textarea rows={5} {...form.register('message')} />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={reply.isPending}>
            {reply.isPending ? 'Sending…' : 'Send Reply'}
          </Button>
        </form>
      </Card>
    </>
  );
}
